import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, MapPin, Mail, FileText } from "lucide-react";
import api, { formatApiErrorDetail, isPlatinumRequired } from "../lib/api";
import { toast } from "sonner";

/**
 * Dedicated, mobile-friendly ensemble-creation page (was a cramped modal).
 * Route: /ensembles/new.
 */
export default function NewEnsemble() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", description: "", location: "", contact_email: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.contact_email) delete payload.contact_email;
      const { data } = await api.post("/ensembles", payload);
      toast.success("Ensemble created");
      navigate(data?.id ? `/ensembles/${data.id}` : "/ensembles", { replace: true });
    } catch (err) {
      if (isPlatinumRequired(err)) {
        toast.error("You've reached the free ensemble limit. Redirecting…");
        setTimeout(() => navigate("/pricing"), 800);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="new-ensemble-page">
      <Link to="/ensembles" className="inline-flex items-center gap-2 text-sm text-ink-600 hover:text-ink-900" data-testid="new-ensemble-back">
        <ArrowLeft size={16} /> All ensembles
      </Link>

      <div>
        <div className="label-overline">New ensemble</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Start an ensemble</h1>
        <p className="text-ink-600 text-sm mt-1">Set the basics now — you can invite members, schedule rehearsals, and add parts after.</p>
      </div>

      <form onSubmit={submit} className="card-base p-5 sm:p-6 space-y-5">
        <div>
          <label className="label-overline block mb-2 inline-flex items-center gap-2"><Users size={12} /> Name</label>
          <input
            className="input-field"
            placeholder="e.g. North London Sinfonia"
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="new-ensemble-name"
          />
        </div>

        <div>
          <label className="label-overline block mb-2 inline-flex items-center gap-2"><MapPin size={12} /> Location <span className="text-ink-400 normal-case">(optional)</span></label>
          <input
            className="input-field"
            placeholder="City or borough"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="new-ensemble-location"
          />
        </div>

        <div>
          <label className="label-overline block mb-2 inline-flex items-center gap-2"><Mail size={12} /> Contact email <span className="text-ink-400 normal-case">(optional)</span></label>
          <input
            className="input-field"
            type="email"
            placeholder="who.handles@bookings.example"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            data-testid="new-ensemble-email"
          />
          <p className="text-xs text-ink-500 mt-1">Used for booker enquiries when relevant. Members won't see this.</p>
        </div>

        <div>
          <label className="label-overline block mb-2 inline-flex items-center gap-2"><FileText size={12} /> Short description <span className="text-ink-400 normal-case">(optional)</span></label>
          <textarea
            rows={4}
            className="input-field"
            placeholder="A friendly amateur strings group rehearsing on Wednesday evenings…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="new-ensemble-description"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2 border-t border-ivory-200">
          <Link to="/ensembles" className="btn-secondary justify-center" data-testid="new-ensemble-cancel">Cancel</Link>
          <button disabled={busy || !form.name.trim()} className="btn-primary justify-center" data-testid="new-ensemble-submit">
            {busy ? "Creating…" : "Create ensemble"}
          </button>
        </div>
      </form>
    </div>
  );
}
